import { ChevronRight, Copy, Pencil, Plus, Printer, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Board,
  FactList,
  FilterBar,
  KpiRow,
  MiniChart,
  RecordLayout,
} from '../../components/patterns/index.ts';
import { RowActions } from '../../components/RowActions.tsx';
import { Tabs } from '../../components/Tabs.tsx';
import { Avatar, Badge, Button, NumCell, PageHeader, Panel, Table } from '../../components/ui.tsx';
import { BADGE_TONE, SampleIcon, sampleIcon, type SampleDomain } from '../samples/index.ts';

// As telas da bancada do estúdio (ADR 0133, parte 3), montadas só com as peças de verdade e os
// dados de um exemplo de negócio: Painel, Lista, Registro e Quadro.

export function Crumb({ parts }: { parts: string[] }) {
  return (
    <p className="mb-1 flex items-center gap-1 text-xs text-muted">
      {parts.map((p, i) => (
        <span key={i} className="flex items-center gap-1">
          {i > 0 && <ChevronRight size={12} aria-hidden />}
          {p}
        </span>
      ))}
    </p>
  );
}

const area = (d: SampleDomain) => d.areas.find((a) => a.id === d.mainArea)!;

export function DashboardScreen({ d }: { d: SampleDomain }) {
  const { t } = useTranslation();
  const max = Math.max(...d.hbar.rows.map((r) => r[1]));
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('style.demo.hello', { name: d.user[0].split(' ')[0] })}
        description={d.painelSub}
      />
      <KpiRow
        items={d.kpis.map(([label, value, dir, delta, note, icon]) => ({
          label,
          value,
          delta: { text: delta, good: dir === 'up' },
          note,
          icon: sampleIcon(icon),
        }))}
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <Panel>
          <MiniChart
            label={`${d.chart.title} (${d.chart.unit})`}
            points={d.chart.values.map((value, i) => ({
              label: new Date(Date.UTC(2026, i, 1)).toLocaleDateString('pt-BR', {
                month: 'short',
                timeZone: 'UTC',
              }),
              value,
            }))}
          />
        </Panel>
        <Panel title={t('style.demo.todo')}>
          <ul className="flex flex-col gap-2.5 text-sm">
            {d.todo.map(([tone, icon, text]) => {
              return (
                <li key={text} className="flex items-start gap-2.5">
                  <SampleIcon
                    name={icon}
                    size={16}
                    aria-hidden
                    className={
                      tone === 'bad' ? 'text-danger' : tone === 'ok' ? 'text-ok' : 'ui-icon-support'
                    }
                  />
                  {text}
                </li>
              );
            })}
          </ul>
        </Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={d.hbar.title}>
          <ul className="flex flex-col gap-2 text-sm">
            {d.hbar.rows.map(([label, n, tone]) => (
              <li
                key={label}
                className="grid grid-cols-[9rem_minmax(0,1fr)_2.5rem] items-center gap-2"
              >
                <Badge tone={BADGE_TONE[tone]}>{label}</Badge>
                <span className="h-2 rounded-full bg-paper">
                  <span
                    className="block h-2 rounded-full bg-accent"
                    style={{ width: `${(n / max) * 100}%` }}
                  />
                </span>
                <span className="text-right tabular-nums">{n}</span>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title={d.top.title}>
          <ul className="flex flex-col divide-y divide-rule text-sm">
            {d.top.rows.map((r) => (
              <li key={r[0]} className="flex items-center justify-between gap-3 py-2">
                <span className="truncate font-medium">{r[0]}</span>
                <span className="text-xs text-muted">{r[1]}</span>
                <span className="tabular-nums">{r[2]}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  );
}

export function ListScreen({ d, onOpen }: { d: SampleDomain; onOpen?: (i: number) => void }) {
  const { t } = useTranslation();
  const [sel, setSel] = useState<number[]>([1]);
  const a = area(d);
  const statuses = d.board.map((b) => b[0]);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Crumb parts={[a.label, d.mainChild]} />
        <PageHeader
          title={d.list.title}
          description={d.list.sub}
          actions={
            <>
              <Button variant="secondary">{t('style.sample.secondary')}</Button>
              <Button>
                <Plus size={16} aria-hidden />
                {d.list.newL}
              </Button>
            </>
          }
        />
      </div>
      <KpiRow
        items={d.kpis.map(([label, value, dir, delta, note, icon]) => ({
          label,
          value,
          delta: { text: delta, good: dir === 'up' },
          note,
          icon: sampleIcon(icon),
        }))}
      />
      <FilterBar
        search={t('style.demo.search')}
        filters={[
          { label: t('style.demo.status'), value: t('style.demo.all') },
          { label: d.list.cols[3]?.[0] ?? '', value: t('style.demo.all') },
        ]}
      >
        {sel.length > 0 && (
          <div className="flex items-center gap-2 rounded-lg bg-brand-soft px-3 py-2 text-sm">
            {t('style.demo.selected', { count: sel.length })}
            <Button variant="secondary" className="min-h-0 py-1 text-xs">
              {statuses[statuses.length - 1]}
            </Button>
          </div>
        )}
        <Table
          head={[
            '',
            ...d.list.cols.map(([label, , kind]) =>
              kind === 'num' ? { label, align: 'right' as const } : label,
            ),
            '',
          ]}
        >
          {d.rows.map((r, i) => (
            <tr key={r.id} onClick={() => onOpen?.(i)} className={onOpen ? 'cursor-pointer' : ''}>
              <td>
                <input
                  type="checkbox"
                  aria-label={r.title}
                  checked={sel.includes(i)}
                  onChange={() =>
                    setSel((s) => (s.includes(i) ? s.filter((x) => x !== i) : [...s, i]))
                  }
                  onClick={(e) => e.stopPropagation()}
                  className="size-4 accent-accent"
                />
              </td>
              {d.list.cols.map(([, key, kind]) => {
                const v = r[key];
                if (kind === 'badge') {
                  const st = v as SampleDomain['rows'][number]['st'];
                  return (
                    <td key={key}>
                      <Badge tone={BADGE_TONE[st[1]]}>{st[0]}</Badge>
                    </td>
                  );
                }
                if (kind === 'num') return <NumCell key={key}>{v as string}</NumCell>;
                return (
                  <td
                    key={key}
                    className={
                      kind === 'strong'
                        ? 'font-medium'
                        : kind === 'id'
                          ? 'font-mono text-xs whitespace-nowrap text-brand-text'
                          : 'whitespace-nowrap text-muted'
                    }
                  >
                    {v as string}
                  </td>
                );
              })}
              <td onClick={(e) => e.stopPropagation()}>
                <RowActions
                  actions={[
                    { label: t('style.demo.edit'), icon: Pencil, onClick: () => {} },
                    { label: t('style.demo.copy'), icon: Copy, onClick: () => {} },
                    {
                      label: t('style.demo.remove'),
                      icon: Trash2,
                      onClick: () => {},
                      danger: true,
                    },
                  ]}
                />
              </td>
            </tr>
          ))}
        </Table>
      </FilterBar>
    </div>
  );
}

export function RecordScreen({ d, index }: { d: SampleDomain; index?: number }) {
  const { t } = useTranslation();
  const r = d.rows[index ?? d.mainIdx]!;
  const tabs = d.rec.tabs.map(([label, count]) => ({ id: label, label, count }));
  const [tab, setTab] = useState(tabs[0]!.id);
  const a = area(d);
  return (
    <div className="flex flex-col gap-4">
      <Crumb parts={[a.label, d.mainChild, r.id]} />
      <RecordLayout
        title={r.title}
        meta={
          <>
            <code className="font-mono text-xs text-muted">{r.id}</code>
            <Badge tone={BADGE_TONE[r.st[1]]}>{r.st[0]}</Badge>
          </>
        }
        actions={
          <>
            <Button variant="secondary">
              <Printer size={16} aria-hidden />
              {d.rec.secondary[0]}
            </Button>
            <Button>{d.rec.primary[0]}</Button>
          </>
        }
        aside={
          <>
            <FactList
              items={[
                ...d.rec.summary.map(([label, value]) => ({ label, value })),
                {
                  label: d.rec.big[0],
                  value: <b className="tabular-nums">{`${d.rec.big[2]}${r.val}`}</b>,
                },
              ]}
            />
            <Panel title={t('style.demo.history')}>
              <ol className="flex flex-col gap-3 text-sm">
                {d.rec.tl.map(([what, when, tone]) => (
                  <li key={what} className="flex gap-2.5">
                    <span
                      aria-hidden
                      className={`mt-1.5 size-2 shrink-0 rounded-full ${tone === 'ok' ? 'bg-ok' : tone === 'warn' ? 'bg-gold' : tone === 'info' ? 'bg-info' : 'bg-control-border'}`}
                    />
                    <span className="flex flex-col">
                      {what}
                      <span className="text-xs text-muted">{when}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>
          </>
        }
      >
        <Tabs items={tabs} value={tab} onChange={setTab} label={r.title} idPrefix="rec" />
        <div
          className="ui-alert flex gap-2 rounded-lg p-3 text-sm"
          style={{ '--c': 'var(--gold)' } as React.CSSProperties}
        >
          <b className="font-semibold">{d.rec.alert[2]}</b> {d.rec.alert[3]}
        </div>
        <Panel>
          <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            {d.rec.fields.map(([label, value, kind]) => (
              <div
                key={label}
                className={`flex flex-col gap-1 ${kind === 'full' || kind === 'ta' ? 'sm:col-span-2' : ''}`}
              >
                <dt className="text-xs text-muted">{label}</dt>
                <dd className="ui-control ui-input flex items-center border px-3">{value}</dd>
              </div>
            ))}
          </dl>
        </Panel>
        <Panel title={d.rec.sub.title}>
          <Table
            head={d.rec.sub.cols.map(([label, kind]) =>
              kind.startsWith('n') ? { label, align: 'right' as const } : label,
            )}
          >
            {d.rec.sub.rows.map((row) => (
              <tr key={row[0]}>
                {row.map((c, i) =>
                  d.rec.sub.cols[i]?.[1].startsWith('n') ? (
                    <NumCell key={i}>{c}</NumCell>
                  ) : (
                    <td key={i} className={i === 0 ? 'font-medium' : 'text-muted'}>
                      {c}
                    </td>
                  ),
                )}
              </tr>
            ))}
          </Table>
        </Panel>
      </RecordLayout>
    </div>
  );
}

export function BoardScreen({ d }: { d: SampleDomain }) {
  const a = area(d);
  return (
    <div className="flex flex-col gap-4">
      <div>
        <Crumb parts={[a.label, d.mainChild]} />
        <PageHeader title={d.list.title} description={d.list.sub} />
      </div>
      <Board
        label={d.list.title}
        columns={d.board.map(([label]) => ({ id: label, title: label }))}
        items={d.rows.filter((r) => r.st[1] !== 'bad')}
        columnOf={(r) => r.st[0]}
        keyOf={(r) => r.id}
        renderCard={(r) => (
          <div className="flex flex-col gap-1.5">
            <span className="font-mono text-xs text-brand-text">{r.id}</span>
            <span className="font-medium">{r.title}</span>
            <span className="flex items-center justify-between gap-2 text-xs text-muted">
              <span className="flex items-center gap-1.5">
                <Avatar name={r.who} size={20} />
                {r.who.split(' ')[0]}
              </span>
              <span className="tabular-nums">{r.val}</span>
            </span>
          </div>
        )}
      />
    </div>
  );
}
